<template>
  <v-dialog :value="value" width="500" @click:outside='close'>
    <v-form ref="editForm">
      <v-card max-width="500" class="mx-auto">
        <v-app-bar dark color="primary">
          <v-toolbar-title>
            <translate> Edit Profile </translate>
          </v-toolbar-title>
        </v-app-bar>
        <v-container class="pa-6 pb-0" fluid grid-list-md>
          <v-layout>
            <v-flex sm12 md6>
              <v-text-field
                :error-messages="nameErrors"
                required
                v-model="formData.name"
                :label="$gettext('First Name') + ' *'"
                @input="$v.formData.name.$touch()"
                @blur="$v.formData.name.$touch()"
              >
              </v-text-field>
            </v-flex>
            <v-flex sm12 md6>
              <v-text-field
                :error-messages="lastNameErrors"
                required
                v-model="formData.lastName"
                :label="$gettext('Last Name') + ' *'"
                @input="$v.formData.lastName.$touch()"
                @blur="$v.formData.lastName.$touch()"
              >
              </v-text-field>
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex sm12>
              <v-text-field
                disabled
                v-model="formData.email"
                :error-messages="emailErrors"
                :label="$gettext('Email') + '*'"
                required
                @input="$v.formData.email.$touch()"
                @blur="$v.formData.email.$touch()"
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex sm12>
              <div class="d-flex flex-column phone-input">
                <div class="d-flex flex-column">
                  <label class="mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    v-model="formData.phoneNumber"
                    class="py-1"
                    @input="handlePhoneNumberValidation(formData.phoneNumber)"
                    v-ripple
                  />
                  <span class="focus-ripple"></span>
                </div>
                <ErrorMessages :error="phoneNumberError" />
              </div>
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex sm12>
              <v-text-field
                v-model="formData.organizationName"
                disabled
                :label="$gettext('Organization Name')"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-container>

        <v-card-actions class="pb-6 pr-6">
          <v-spacer></v-spacer>

          <v-btn text @click="close">
            <translate> CANCEL </translate>
          </v-btn>

          <v-btn @click="update" color="primary">
            <translate> CONTINUE </translate>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required, email } from 'vuelidate/lib/validators';
import phoneNumberMixin from '@/mixins/phoneNumberMixin';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css'
import ErrorHandler from '@/utils/ErrorHandler'
import profile from '@/services/apiCalls/profile';

export default {
  name: 'ProfileEditDialog',

  mixins: [validationMixin, phoneNumberMixin],

  props: {
    value: {
      type: Boolean,
      required: true,
    }
  },

  data() {
    return {
      formData: {
        name: null,
        lastName: null,
        email: null,
        organizationName: null,
        phoneNumber: null,
      },
    }
  },

  validations: {
    formData: {
      email: { required, email },
      name: { required },
      lastName: { required },
    },
  },

  computed: {
    nameErrors() {
      const errors = [];

      if (!this.$v.formData.name.$dirty) {
        return errors;
      }

      !this.$v.formData.name.required &&
      errors.push(`${this.$gettext('First Name')} ${this.$gettext('is required')}`);

      return errors;
    },

    lastNameErrors() {
      const errors = [];

      if (!this.$v.formData.lastName.$dirty) {
        return errors;
      }

      !this.$v.formData.lastName.required &&
      errors.push(`${this.$gettext('Last Name')} ${this.$gettext('is required')}`);
      return errors;
    },

    emailErrors() {
      const errors = [];

      if (!this.$v.formData.email.$dirty) {
        return errors;
      }

      !this.$v.formData.email.email && errors.push(`${this.$gettext('Must be valid e-mail')}`);
      !this.$v.formData.email.required &&
      errors.push(`${this.$gettext('Email')} ${this.$gettext('is required')}`);
      return errors;
    },
  },

  watch: {
    value(v) {
      if (v) {
        this.onOpen();
      }
    },
  },

  methods: {
    onOpen() {
      const { given_name, family_name, email } = this.$store.state.auth.user;
      const { name } = this.$store.state.auth.orgInfo;

      this.formData.name = given_name;
      this.formData.lastName = family_name;
      this.formData.email = email;
      this.formData.phoneNumber = this.$store.state.auth.user['custom:cell_phone'];
      this.formData.organizationName = name;

      this.$nextTick(() => {
        this.init();
      });
    },

    close() {
      this.$emit('close-dialog');
      Object.keys(this.formData).forEach((key) => {
        this.formData[key] = null;
      })
      this.$refs.editForm.resetValidation();
      this.intlTelInputInstance.destroy();
      this.intlTelInputInstance = null;
    },

    init() {
      this.handleDefaultCountry();

      const input = document.querySelector('#phone');

      this.intlTelInputInstance = intlTelInput(input, {
        separateDialCode: true,
        initialCountry: this.defaultCountry,
      });

      this.countryCode = this.intlTelInputInstance.getSelectedCountryData();
      if (this.formData.phoneNumber) {
        this.formData.phoneNumber = this.formData.phoneNumber.replace('+', '').replace(this.countryCode.dialCode, '');
      }

      input.addEventListener('countrychange', () => {
        this.countryCode = this.intlTelInputInstance.getSelectedCountryData()
      });
    },

    async update() {
      this.handlePhoneNumberValidation(this.formData.phoneNumber);

      if (!this.phoneNumberError && !this.$v.invalid) {
        try {
          this.$store.commit('spinner', true);
          const { data: { updateProfile: { firstName, lastName, phoneNumber }} } = await profile.updateProfile(
            this.$store.state.auth.user.sub,
            {
              firstName: this.formData.name,
              lastName: this.formData.lastName,
              phoneNumber: '+' + this.countryCode.dialCode + this.formData.phoneNumber,
            }
          );
          const user = {
            ...JSON.parse(localStorage.getItem('currentUser')),
            'custom:cell_phone': phoneNumber,
            given_name: firstName,
            family_name: lastName,
          };
          this.$store.commit('auth/setAuthState', {
            isLoggedIn: true,
            user,
          })
          localStorage.setItem('currentUser', JSON.stringify(user));
          this.$emit('update');
          this.close();
        } catch (e) {
          ErrorHandler(e, this);
        } finally {
          this.$store.commit('spinner', false);
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
.phone-input {
  &:focus-within {
    label {
      color: #000;
    }
  }

  div {
    position: relative;

    label {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.54);
    }
    input {
      width: 100%;
      border-bottom: 2px solid #c3c3c3;
      transition: 0.25s linear;
      outline: 2px solid transparent;
      // &:focus {
      //     border-bottom-color: #1976d2;
      // }
    }

    .focus-ripple {
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 0;
      height: 2px;
      background-color: #000;
      transition: 0.4s;
    }
  }

  &:focus-within .focus-ripple {
    width: 100%;
    transition: 0.4s;
    left: 0;
  }
}
</style>